import { parsePhoneNumberFromString } from 'libphonenumber-js';

/**
 * Result of reading a stored phone number for `react-phone-number-input`.
 *
 * - `value`: an E.164 string safe to hand to the library, or `undefined`.
 * - `unrecognized`: the original stored text when it could not be turned into
 *   E.164. The caller must surface it; dropping it silently hides user data.
 */
export interface StoredPhoneValue {
  value: string | undefined;
  unrecognized: string | undefined;
}

const E164_SHAPE = /^\+\d+$/;

/**
 * Normalises a stored phone number to E.164 before it reaches
 * `react-phone-number-input`.
 *
 * The library accepts only E.164 and calls `console.error` for anything else
 * (Sentry BALLEE-JJ). Profiles hold numbers written by several paths, and many
 * are loosely formatted or national format ("078 ...").
 *
 * Only numbers that carry their own country code are resolved: a leading `+`
 * or the `00` international prefix, with any formatting. A national number is
 * NOT resolved against a default country, because a foreign national number of
 * the right length (a French "06 ...") would come out as a valid-looking Swiss
 * number and be saved as such on the next edit. Those are returned as
 * `unrecognized` so the user re-enters them with a country code.
 */
export function toStoredPhoneValue(
  raw: string | null | undefined,
): StoredPhoneValue {
  const compact = raw ? raw.replace(/\s+/g, '') : '';

  if (!compact) {
    return { value: undefined, unrecognized: undefined };
  }

  if (E164_SHAPE.test(compact)) {
    return { value: compact, unrecognized: undefined };
  }

  const international = compact.startsWith('00')
    ? `+${compact.slice(2)}`
    : compact;

  if (international.startsWith('+')) {
    const parsed = parsePhoneNumberFromString(international, {
      extract: false,
    });

    if (parsed?.isValid()) {
      return { value: parsed.number, unrecognized: undefined };
    }
  }

  return { value: undefined, unrecognized: raw?.trim() };
}
